import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import BasicActivityInfo from './BasicActivityInfo';
import { createTheoryActivitySubmitValidation } from './activityValidation';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import {
  TheoryActivityDto,
  NoteDto,
  PrivilegeEnum,
  StatusDto,
  VisibilityDto,
  UpdateTheoryActivityRequest,
} from '@/api/ApiClient';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { getVisibilityTypeDisplay } from '@/lib/types/visibilityTypes';

import { useUserResolution } from '@/lib/options/useUserResolution';
import DetailField from '@/components/common/DetailField';
import { OutlinedPaper, FlexWrapBox } from '@/components/common/StyledComponents';
import { Footer } from '@/components/details/Footer';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import { useBackendStore } from '@/stores/backendStore';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useTheoryActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import InlineImages from '@/components/common/InlineImages';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useAuthStore } from '@/stores/authStore';
import { canUpdateNote } from '@/types/tree/actions/noteFilePrivilegeHelpers';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createTheoryActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

export const TheoryActivityDetails: React.FC = () => {
  const backend = useBackendStore.getState().getBackend();
  const { resolveUser } = useUserResolution();
  const { setDataModified, updateData, getNotes } = useTree();
  const { selectedNode } = useTreeSelection();
  const { isAuthorized } = usePrivileges();
  const currentUser = useAuthStore((s) => s.currentUser?.id);

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  const data = useTheoryActivityData(selectedNode);

  // Get notes from selectedNode to avoid infinite re-renders
  const notes = selectedNode ? getNotes(selectedNode.dataId) || [] : [];
  const { showSnackbar } = useSnackbar();
  const [editingNote, setEditingNote] = useState<NoteDto | null>(null);

  // Track manual edit mode state (when user clicks "Edit Details" button)
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Validation state
  const [descError, setDescError] = useState(false);
  const [methodError, setMethodError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { submitWithValidation } = useSubmitWithValidation();

  // Centralized date handling utility
  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? new Date(date.toDateString()) : undefined;
  };

  if (!data || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  const handleModification = (modified: Partial<TheoryActivityDto>) => {
    const normalized: Partial<TheoryActivityDto> = { ...modified };
    if ('simulationTechnique' in normalized) {
      normalized.simulationTechnique = nullIfBlank(normalized.simulationTechnique);
    }
    const updated = { ...data, ...normalized };
    updateData(selectedNode.dataId, updated);
    setDataModified(selectedNode.dataId, true);

    // Clear validation errors when user starts typing
    if (modified.desc !== undefined) {
      setDescError(false);
    }
    if (modified.simulationTechnique !== undefined) {
      setMethodError(false);
      clearFieldError('simulationTechnique');
    }
    if (modified.software !== undefined) {
      clearFieldError('software');
    }
    if (modified.version !== undefined) {
      clearFieldError('version');
    }
  };

  // Clear field errors when user starts editing
  const clearFieldError = (fieldKey: string) => {
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Validation function
  const isValid = () => {
    if (!data) return false;
    return validateRequiredToSave(createTheoryActivitySaveValidation(data)).isValid;
  };

  const handleSave = async () => {
    if (!data.id || !selectedNode) return;

    const saveValidation = validateRequiredToSave(createTheoryActivitySaveValidation(data));
    setDescError(!!saveValidation.fieldErrors.desc);
    setMethodError(!!saveValidation.fieldErrors.simulationTechnique);
    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields', 'error');
      return;
    }

    try {
      const request: UpdateTheoryActivityRequest = { ...data };
      const updated = await backend.theoryActivity.update(data.id, request);
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after successful save
      setFieldErrors({});
      showSnackbar('Theory activity saved successfully', 'success');
    } catch (error) {
      log.error('[TheoryActivityDetails] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save theory activity.', 'error');
    }
  };

  // Custom submit handler with validation
  const handleSubmit = async (): Promise<boolean> => {
    if (!data || !selectedNode) return false;
    const validationFields = createTheoryActivitySubmitValidation(data);
    const result = await submitWithValidation(selectedNode, validationFields, 'Theory Activity');
    if (!result.success && result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
      setIsManuallyEditing(true); // Switch to edit mode to show validation errors
    } else {
      setFieldErrors({});
    }
    return result.success;
  };

  // Handler for Footer's setIsReadOnly callback
  // When value === false: user clicked "Edit Details" → enter edit mode
  // When value === true: user clicked "Cancel" → exit edit mode and clear modifications
  const setIsReadOnly = (value: boolean) => {
    setIsManuallyEditing(!value); // false = edit mode, true = read-only mode
    if (value && selectedNode) {
      // Exiting edit mode: clear any unsaved modifications
      setDataModified(selectedNode.dataId, false);
    }
  };

  log.debug('[TheoryActivityDetails] rendering, with notes', notes);

  const isSubmitted = data?.status === StatusDto.Submitted;

  const renderNoteSection = (note: NoteDto | undefined, defaultTitle: string) => {
    const isEditing = editingNote?.id === note?.id;
    const canUpdate = canUpdateNote(isAuthorized, note ?? null, currentUser);
    const editTooltip = canUpdate ? 'Edit note' : 'No permission to edit this note';

    return (
      <Box key={note?.id}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {note?.title || defaultTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {note?.lastChangedDate ? format(new Date(note.lastChangedDate), 'M/d/yyyy, h:mm a') : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {note?.lastChangedByUser ? resolveUser(note.lastChangedByUser) : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {note?.visibility ? getVisibilityTypeDisplay(note.visibility) : ''}
                </Typography>
                {!isReadOnly && canUpdate ? (
                  <Tooltip title={editTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingNote(note || { title: defaultTitle, text: '', visibility: VisibilityDto.Internal });
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {isEditing && !isReadOnly && editingNote ? (
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editingNote.title || ''}
                  onChange={(e) => setEditingNote((prev) => (prev ? { ...prev, title: e.target.value } : null))}
                  margin="normal"
                  size="small"
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Content
                  </Typography>
                  <RichTextEditor
                    value={editingNote.text || ''}
                    onChange={(content) => setEditingNote((prev) => (prev ? { ...prev, text: content } : null))}
                  />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Click outside to save
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                {note?.text ? (
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: note.text }}
                    sx={{ whiteSpace: 'pre-wrap' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No content available
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <BasicActivityInfo
            activity={data}
            isReadOnly={isReadOnly}
            isSubmitted={isSubmitted}
            handleModification={handleModification}
            convertDateForBackend={convertDateForBackend}
            hideVisibility={isReadOnlyNode}
          />

          {/* Image Section */}
          <InlineImages title="Activity Images" />

          {/* Activity Details */}
          <OutlinedPaper elevation={0} variant="outlined">
            <FlexWrapBox>
              {/* Activity Name */}
              <DetailField label="Activity Name" value={data.desc || ''} isReadOnly={isReadOnly} required>
                <DebouncedTextField
                  value={data.desc || ''}
                  onUpdate={(value) => handleModification({ desc: value })}
                  fullWidth
                  size="small"
                  error={descError}
                  helperText={descError ? 'Activity Name is required' : ''}
                />
              </DetailField>

              {/* Method */}
              <DetailField
                label="Method"
                value={data.simulationTechnique || ''}
                isReadOnly={isReadOnly}
                requiredToSubmit
                error={fieldErrors.simulationTechnique}
              >
                <DebouncedTextField
                  value={data.simulationTechnique || ''}
                  onUpdate={(value) => handleModification({ simulationTechnique: value })}
                  fullWidth
                  size="small"
                  error={!!fieldErrors.simulationTechnique || methodError}
                  helperText={fieldErrors.simulationTechnique || (methodError ? 'Method is required' : '')}
                />
              </DetailField>

              {/* Runtime */}
              <DetailField label="Runtime (CPU hours)" value={data.runtime || ''} isReadOnly={isReadOnly}>
                <DebouncedTextField
                  value={data.runtime?.toString() || ''}
                  onUpdate={(value) => handleModification({ runtime: value ? parseFloat(value) : undefined })}
                  fullWidth
                  size="small"
                  type="number"
                />
              </DetailField>
            </FlexWrapBox>

            <FlexWrapBox>
              {/* Software */}
              <DetailField
                label="Software"
                value={data.software || ''}
                isReadOnly={isReadOnly}
                requiredToSubmit
                error={fieldErrors.software}
              >
                <DebouncedTextField
                  value={data.software || ''}
                  onUpdate={(value) => handleModification({ software: value })}
                  fullWidth
                  size="small"
                  error={!!fieldErrors.software}
                  helperText={fieldErrors.software}
                />
              </DetailField>

              {/* Version */}
              <DetailField
                label="Version"
                value={data.version || ''}
                isReadOnly={isReadOnly}
                requiredToSubmit
                error={fieldErrors.version}
              >
                <DebouncedTextField
                  value={data.version || ''}
                  onUpdate={(value) => handleModification({ version: value })}
                  fullWidth
                  size="small"
                  error={!!fieldErrors.version}
                  helperText={fieldErrors.version}
                />
              </DetailField>

              {/* Link */}
              <DetailField label="Link" value={data.link || ''} isReadOnly={isReadOnly}>
                <DebouncedTextField
                  value={data.link || ''}
                  onUpdate={(value) => handleModification({ link: value })}
                  fullWidth
                  size="small"
                />
              </DetailField>
            </FlexWrapBox>
          </OutlinedPaper>

          {/* Notes Section */}
          {notes && notes.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {notes.map((note: NoteDto) => renderNoteSection(note, note.title || ''))}
            </Box>
          ) : null}
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        {isReadOnlyNode ? (
          <ReadOnlyFooter
            lastChangedDate={data.lastChangedDate}
            lastChangedBy={data.lastChangedByUser || 'Unknown'}
            status={data.status || 'Draft'}
          />
        ) : (
          <Footer
            lastChangedDate={data.lastChangedDate}
            lastChangedBy={resolveUser(data.lastChangedByUser || '')}
            status={data.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={setIsReadOnly}
            onSave={handleSave}
            privilege={PrivilegeEnum.TheoryActivity}
            isFormValid={isValid()}
            onSubmit={handleSubmit}
          />
        )}
      </Box>
    </Box>
  );
};
